import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { DEFAULT_PAYMENT_METHODS, PaymentMethodSettings, normalisePaymentMethods } from '@/hooks/useOrgSettings';
import { normalisePaypalMeHandle } from '@/lib/paypal';
import { resolveCashEnabled } from '@/lib/bookingPayment';
import { DEFAULT_PAYMENT_POLICY, PaymentPolicy, normalisePaymentPolicy } from '@/lib/paymentPolicy';

export interface PaymentsAdminData {
  companyInfoId: string;
  methods: PaymentMethodSettings;
  policy: PaymentPolicy;
}

export function usePaymentsAdminData(
  orgId: string | null | undefined,
  orgLoading: boolean,
) {
  return useQuery({
    queryKey: ['payments-admin', orgId],
    enabled: Boolean(orgId) && !orgLoading,
    queryFn: async (): Promise<PaymentsAdminData> => {
      const { data, error } = await supabase
        .from('company_info')
        .select('id, payment_methods, payment_policy')
        .eq('organization_id', orgId!)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        return {
          companyInfoId: '',
          methods: DEFAULT_PAYMENT_METHODS,
          policy: DEFAULT_PAYMENT_POLICY,
        };
      }

      return {
        companyInfoId: data.id,
        methods: normalisePaymentMethods(data.payment_methods as Parameters<typeof normalisePaymentMethods>[0]),
        policy: normalisePaymentPolicy(data.payment_policy as Partial<PaymentPolicy> | null | undefined),
      };
    },
  });
}

export interface SavePaymentsInput {
  companyInfoId: string;
  methods: PaymentMethodSettings;
  policy: PaymentPolicy;
  /**
   * Whether the connected Stripe account may take card payments. `false` forces
   * checkout_enabled off; `undefined` (status unknown) leaves it as loaded.
   */
  checkoutAllowed?: boolean;
}

export function useSavePayments(orgId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ companyInfoId, methods, policy, checkoutAllowed }: SavePaymentsInput) => {
      const handle = normalisePaypalMeHandle(methods.paypal.paypal_me);
      const paypalActive = Boolean(methods.paypal.enabled && handle);

      const checkoutEnabled = checkoutAllowed === false ? false : (methods.stripe.checkout_enabled ?? false);
      const paymentMethods: PaymentMethodSettings = {
        // Cash may only be switched off when customers have another way to pay
        // (card that can take charges, or PayPal). Pay to book.
        cash_in_person: {
          enabled: resolveCashEnabled({
            requested: methods.cash_in_person.enabled,
            cardReady: checkoutAllowed === true && checkoutEnabled,
            paypalReady: paypalActive,
          }),
          available: true,
        },
        stripe: {
          enabled: methods.stripe.enabled,
          available: methods.stripe.available,
          status: methods.stripe.status ?? 'coming_soon',
          payment_link: methods.stripe.payment_link?.trim() || '',
          checkout_enabled: checkoutEnabled,
        },
        paypal: {
          enabled: paypalActive,
          available: paypalActive,
          status: paypalActive ? 'active' : 'not_set_up',
          paypal_me: handle ?? '',
        },
      };

      const { error } = await supabase
        .from('company_info')
        .update({ payment_methods: paymentMethods, payment_policy: policy })
        .eq('id', companyInfoId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments-admin', orgId] });
    },
  });
}
